import { Fragment } from 'react';
import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { EmployeeList } from '@kontor/shared';
import { api, queryString } from '../../api';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { SortTh } from '../../components/SortTh';
import { useSession } from '../../session';
import { EmployeeAvatar } from './EmployeePhoto';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import './hr.css';

const LETTERS = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ', '#'];
/** Anfangsbuchstabe des Nachnamens ohne Akzente (Ö wird O), alles andere steht unter „#“ */
const letterOf = (name: string) => {
  const c = name.trim().charAt(0).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
  return /^[A-Z]$/.test(c) ? c : '#';
};

/** Liste der Mitarbeiter mit Suche und Filtern nach Status und Abteilung. Sensible Daten stehen hier nie. */
export function EmployeesPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const q = params.get('q') ?? '';
  // Vorgabe: nur aktive Mitarbeiter. Deaktivierte oder alle zeigt die Liste erst, wenn man es im Filter wählt.
  const status = params.get('status') ?? '';
  const department = params.get('department') ?? '';
  // Sortierung per Klick auf die Spalte. Buchstabenzeilen und Buchstabenleiste gibt es nur bei der Sortierung nach Namen.
  const sort = params.get('sort') ?? 'name';
  const byName = sort === 'name' || sort === '-name';

  const list = useQuery({
    queryKey: ['employees', { q, status, department, sort }],
    queryFn: () => api<EmployeeList>('GET', `/employees${queryString({ q, status: status === 'all' ? '' : status || 'active', department, sort: sort === 'name' ? '' : sort })}`),
    placeholderData: keepPreviousData,
  });

  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    setParams(merged);
  };
  const onSort = (next: string) => update({ sort: next === 'name' ? '' : next });
  const fields: FilterField[] = [
    { key: 'status', label: 'Status', type: 'select', options: [{ value: '', label: 'Aktive' }, { value: 'inactive', label: 'Deaktivierte' }, { value: 'all', label: 'Alle' }] },
    ...((list.data?.meta.departments.length ?? 0) > 0
      ? [{ key: 'department', label: 'Abteilung', type: 'select' as const, options: [{ value: '', label: 'Alle Abteilungen' }, ...list.data!.meta.departments.map((d) => ({ value: d, label: d }))] }]
      : []),
  ];

  return (
    <>
      <div className="page-head">
        <h1>Mitarbeiter</h1>
        <span className="muted">{list.data ? `${list.data.meta.total} gefunden` : ''}</span>
        {can('hr.write') && <Link className="btn btn--primary btn--sm page-head__action" to="/mitarbeiter/neu">Neuer Mitarbeiter</Link>}
      </div>

      <FilterBar
        search={{ value: q, placeholder: 'Name, Personalnummer, Position', onSubmit: (v) => update({ q: v }) }}
        fields={fields}
        values={{ status, department }}
        onChange={(key, value) => update({ [key]: value })}
        onReset={() => setParams(new URLSearchParams())}
      />

      {list.isError && <div className="alert alert--error">{(list.error as Error).message}</div>}

      <div className="alpha-wrap">
      {byName && <AlphaRail letters={new Set((list.data?.data ?? []).map((e) => letterOf(e.lastName)))} />}
      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <SortTh label="Name" sortKey="name" sort={sort} onSort={onSort} />
              <SortTh label="Personalnr." sortKey="personnelNumber" sort={sort} onSort={onSort} />
              <SortTh label="Position" sortKey="position" sort={sort} onSort={onSort} />
              <SortTh label="Abteilung" sortKey="department" sort={sort} onSort={onSort} />
              <SortTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={5} className="muted">Wird geladen …</td></tr>}
            {list.data?.data.length === 0 && (
              <tr>
                <td colSpan={5} className="empty">{q || status || department ? 'Keine Mitarbeiter zu dieser Suche.' : 'Noch keine Mitarbeiter angelegt.'}</td>
              </tr>
            )}
            {list.data?.data.map((e, index, all) => (
              <Fragment key={e.id}>
                {byName && (index === 0 || letterOf(all[index - 1]!.lastName) !== letterOf(e.lastName)) && (
                  <tr className="letter-row" id={`letter-${letterOf(e.lastName)}`}><th colSpan={5} scope="colgroup">{letterOf(e.lastName)}</th></tr>
                )}
              <ExpandRow
                className={e.isActive ? undefined : 'row--muted'}
                summary={
                  <div className="employee-name">
                    <EmployeeAvatar employee={e} size={36} />
                    <div className="employee-name__text">
                      <Summary title={<Link to={`/mitarbeiter/${e.id}`} className="strong-link">{e.lastName}, {e.firstName}</Link>} line2={[e.personnelNumber, e.position].filter(Boolean).join(' · ')} />
                    </div>
                  </div>
                }
              >
                <td className="cell-desktop-title">
                  <div className="employee-name">
                    <EmployeeAvatar employee={e} size={36} />
                    <div>
                      <Link to={`/mitarbeiter/${e.id}`} className="strong-link">{e.lastName}, {e.firstName}</Link>
                      {e.userId && <div className="muted small">Hat einen Zugang zu Wirkia</div>}
                    </div>
                  </div>
                </td>
                <td data-label="Personalnr.">{e.personnelNumber}</td>
                <td data-label="Position">{e.position ?? '–'}</td>
                <td data-label="Abteilung">{e.department ?? '–'}</td>
                <td data-label="Status"><span className={`badge ${e.isActive ? 'badge--won' : ''}`}>{e.isActive ? 'Aktiv' : 'Deaktiviert'}</span></td>
                <td data-label="Zugang" className="cell-mobile-only">{e.userId ? 'Hat einen Zugang zu Wirkia' : 'Kein Zugang'}</td>
              </ExpandRow>
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </>
  );
}

/**
 * Buchstabenleiste am linken Rand wie in der Kontaktliste eines Telefons (nur unter 720 Pixel). Ein Tipp oder Wischen über die Leiste springt zum Buchstaben,
 * Buchstaben ohne Mitarbeiter sind grau.
 */
function AlphaRail({ letters }: { letters: Set<string> }) {
  const jump = (letter: string) => document.getElementById(`letter-${letter}`)?.scrollIntoView({ block: 'start' });
  const onMove = (e: React.PointerEvent) => {
    if (e.buttons === 0 && e.pointerType === 'mouse') return;
    const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
    const letter = el?.closest<HTMLElement>('[data-letter]')?.dataset.letter;
    if (letter && letters.has(letter)) jump(letter);
  };
  return (
    <nav className="alpha-rail" aria-label="Alphabet" onPointerMove={onMove}>
      {LETTERS.map((l) => (
        <button key={l} type="button" data-letter={l} disabled={!letters.has(l)} aria-label={`Zu Buchstabe ${l}`} onClick={() => jump(l)}>{l}</button>
      ))}
    </nav>
  );
}
